import React from "react";

import type { CodeComponentProps } from "@perceptron/utils/markdownParser/types";

import ClipAnnotationText, { ClipAnnotationTextProps } from "@demo/components/Annotation/ClipAnnotationText";
import CollectionAnnotationText, {
  CollectionAnnotationTextProps,
} from "@demo/components/Annotation/CollectionAnnotationText";
import PointAnnotationText, { PointAnnotationTextProps } from "@demo/components/Annotation/PointAnnotationText";
import TrackAnnotationText, { TrackAnnotationTextProps } from "@demo/components/Annotation/TrackAnnotationText";

import { ToolCallSlot, TOOL_CALL_SLOT_TAG } from "./tool-calls/ToolCallSlot";

export const createMarkdownComponents = () => ({
  // Custom styling for code blocks following @gpriess accessibility recommendation
  code: ({ inline, children, ...props }: CodeComponentProps) => {
    return (
      <div className="markdown my-1 overflow-x-auto rounded-lg bg-gray-900 p-4 text-green-300">
        <code {...props}>{children}</code>
      </div>
    );
  },

  // Custom styling for headings
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mb-4 mt-6 text-2xl font-bold first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mb-3 mt-5 text-xl font-bold first:mt-0">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-2 mt-4 text-lg font-bold first:mt-0">{children}</h3>
  ),

  // Custom styling for paragraphs
  p: ({ children }: { children?: React.ReactNode }) => <span className="inline">{children}</span>,

  // Custom styling for lists
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-4 list-outside list-disc space-y-1 pl-6">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-4 list-outside list-decimal space-y-1 pl-6">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="ml-4">{children}</li>,

  // Custom styling for blockquotes
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="mb-4 border-l-4 border-gray-500 py-2 pl-4 italic text-gray-300">{children}</blockquote>
  ),

  // Custom styling for tables
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="mb-4 overflow-x-auto">
      <table className="min-w-full border border-gray-600">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border border-gray-600 px-4 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border border-gray-600 px-4 py-2">{children}</td>
  ),

  // Custom styling for links
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a className="text-blue-400 underline hover:text-blue-300" href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  ),

  // Custom styling for horizontal rules
  hr: () => <hr className="my-6 border-gray-600" />,

  /* Custom Tags */
  clip: (props: ClipAnnotationTextProps) => {
    return <ClipAnnotationText {...props} />;
  },
  point: (props: PointAnnotationTextProps) => {
    return <PointAnnotationText {...props} />;
  },
  "point-box": (props: PointAnnotationTextProps) => {
    return <PointAnnotationText {...props} />;
  },
  polygon: (props: PointAnnotationTextProps) => {
    return <PointAnnotationText {...props} />;
  },
  collection: (props: CollectionAnnotationTextProps) => {
    return <CollectionAnnotationText {...props} />;
  },
  // Aliased from `<track>` by remarkTrackAlias (HTML's `<track>` is a void
  // element and would flatten nested waypoints to siblings).
  "vlm-track": (props: TrackAnnotationTextProps) => {
    return <TrackAnnotationText {...props} />;
  },
  think: ({ children }: { children?: React.ReactNode }) => {
    return <span className="m-0 inline p-0">{children}</span>;
  },
  // Inserted by AgentOutput where each tool call began in the reasoning; renders those calls.
  [TOOL_CALL_SLOT_TAG]: ({ at }: { at?: string }) => {
    const parsed = Number(at);
    return Number.isInteger(parsed) ? <ToolCallSlot at={parsed} /> : null;
  },
});
